import { getAnswerStateStyles } from "../../../../constants/StudentResultAnswerColors";
import { getTeacherCommentTone } from "../../../../constants/StudentResultCommentTones";
import {
    StudentResultAnswerStates,
    type StudentResultAnswer,
    type StudentResultExercise,
} from "../../../../helpers/StudentResultModel";
import useBreakpoints, { Breakpoint } from "../../../../hooks/useBreakpoints";
import StudentResultAnswerCheck from "../StudentResultAnswerCheck";
import StudentResultAnswerRow from "../StudentResultAnswerRow";
import StudentResultAnswerScore from "../StudentResultAnswerScore";
import StudentResultAnswerStateLabel from "../StudentResultAnswerStateLabel";
import StudentResultNoAnswerPlaceholder from "../StudentResultNoAnswerPlaceholder";

interface StudentResultAnswersConnectWordsProps {
    exercise: StudentResultExercise;
}

const CARD_BASE_CLASSES =
    "flex items-center justify-center min-w-0 min-h-xxxl p-s rounded-medium border-2 border-p6-grey-light font-semibold text-m text-center wrap-anywhere";

/**
 * Connect words: each pair as the student left it, the thick edge between the cards coloured by the
 * state rather than by the colour the student picked while connecting. The card text stays grey as
 * in the design — only the surface and the joint carry the verdict.
 */
const StudentResultAnswersConnectWords = (props: StudentResultAnswersConnectWordsProps) => {
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const renderPair = (answer: StudentResultAnswer) => {
        const styles = getAnswerStateStyles(answer.state);
        const isUnanswered = answer.state === StudentResultAnswerStates.NO_ANSWER;

        return (
            <div className={`grid grid-cols-2 ${getStyleBasedOnBreakpoint(Breakpoint.MD, "gap-x-l", "gap-x-s")}`}>
                <div className={`${CARD_BASE_CLASSES} bg-white text-p6-grey-dark border-r-[8px] ${styles.jointLeft}`}>
                    {answer.question}
                </div>
                <div
                    className={`${CARD_BASE_CLASSES} border-l-[8px] ${styles.jointRight} ${styles.background} ${
                        isUnanswered ? "text-p6-grey-mid" : "text-p6-grey-dark"
                    }`}
                >
                    <StudentResultAnswerStateLabel state={answer.state} />
                    {isUnanswered ? <StudentResultNoAnswerPlaceholder /> : answer.studentAnswer}
                </div>
            </div>
        );
    };

    return (
        <ul
            className="flex flex-col"
            data-testid="student-result-answers-connect-words"
        >
            {props.exercise.answers.map((answer, index) => (
                <StudentResultAnswerRow
                    key={answer.id}
                    isLast={index === props.exercise.answers.length - 1}
                    comment={answer.teacherComment}
                    tone={getTeacherCommentTone(answer.state)}
                >
                    <div className="flex flex-col gap-xs">
                        <div className="flex items-center justify-end gap-s">
                            <StudentResultAnswerCheck state={answer.state} />
                            <StudentResultAnswerScore
                                answer={answer}
                                isScored={props.exercise.isScored}
                            />
                        </div>
                        {renderPair(answer)}
                    </div>
                </StudentResultAnswerRow>
            ))}
        </ul>
    );
};

export default StudentResultAnswersConnectWords;
